import { DataTableColumnHeader } from 'components/ui/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import {
  DataTableDrawer,
  IDataTableDrawer,
} from 'components/ui/DataTableDrawer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { Formatter } from 'utils';
import { addDays } from 'date-fns';

export function useColumns() {
  const columns: ColumnDef<any>[] = [
    {
      id: 'company',
      accessorKey: 'company.tradeName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Cliente" />
      ),
      cell: ({ row }) => {
        let value = row.original.company.tradeName;
        if (row.original.company.ie) value += ` - ${row.original.company.ie}`;
        return value;
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'fazenda',
      accessorKey: 'company.addressComplement',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Fazenda" />
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'Código',
      accessorKey: 'product.code',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Código" />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'Descrição',
      accessorKey: 'product.name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Descrição" />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'Lote',
      accessorKey: 'batch',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Lote" />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    // {
    //   id: 'TP',
    //   accessorKey: 'tp',
    //   header: ({ column }) => (
    //     <DataTableColumnHeader column={column} title="TP" />
    //   ),
    //   enableSorting: true,
    //   enableHiding: true,
    // },
    {
      id: 'UN',
      accessorKey: 'productUnit.name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="UN" />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'QTD',
      accessorKey: 'quantity',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="QTD" />
      ),
      cell: ({ row }) => {
        return row.original.quantity.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
        });
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'Entrada',
      accessorKey: 'entranceDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Entrada" />
      ),
      cell: ({ row }) => {
        if (!row.original.entranceDate) return '---';
        const date = new Date(row.original.entranceDate);
        return date.toLocaleDateString('pt-BR');
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'Fab',
      accessorKey: 'manufacturingDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Fab" />
      ),
      cell: ({ row }) => {
        if (!row.original.manufacturingDate) return '---';
        const date = new Date(row.original.manufacturingDate);
        return date.toLocaleDateString('pt-BR');
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'Venc',
      accessorKey: 'expirationDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Venc" />
      ),
      cell: ({ row }) => {
        if (!row.original.expirationDate) return '---';

        let bgColor = 'bg-transparent';

        if (new Date(row.original.expirationDate) <= addDays(new Date(), 90))
          bgColor = 'bg-yellow-500 text-white';
        if (new Date(row.original.expirationDate) <= addDays(new Date(), 60))
          bgColor = 'bg-orange-500 text-white';
        if (new Date(row.original.expirationDate) <= addDays(new Date(), 30))
          bgColor = 'bg-red-500 text-white';

        const date = new Date(row.original.expirationDate);

        return (
          <div className={`rounded float-start py-1 px-2 ${bgColor}`}>
            {date.toLocaleDateString('pt-BR')}
          </div>
        );
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'Valor',
      accessorKey: 'totalPrice',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Valor" />
      ),
      cell: ({ row }) => {
        return Formatter.currency(row.original.totalPrice);
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'NF',
      accessorKey: 'invoiceNumber',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="NF" />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'updatedAt',
      accessorKey: 'updatedAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Última atualização" />
      ),
      cell: ({ row }) => {
        const date = new Date(row.original.updatedAt);
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'local',
      accessorKey: 'stockType',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="" />
      ),
      cell: ({ row }) => {
        const colorClass =
          row.original.stockType === 'AG' ? 'bg-lime-600' : 'bg-violet-600';

        return (
          <span
            className={`inline-block text-slate-50 px-2 py-1.5 rounded text-xs font-bold ${colorClass}`}
          >
            {row.original.stockType}
          </span>
        );
      },
      enableSorting: false,
    },
    {
      id: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="" />
      ),
      cell: ({ row }) => {
        const colorClass =
          row.original.stockType === 'AG' ? 'bg-lime-600' : 'bg-violet-600';

        const content = (
          <>
            <div>
              <span
                className={`inline-block mb-4 bg-lime-600 text-slate-50 p-2 rounded text-xs font-bold ${colorClass}`}
              >
                {row.original.stockType}
              </span>
              <h2 className="font-sans text-xs text-slate-600 font-normal">
                {row.original.product.name}
              </h2>
              <p className="font-GilroyExtraBold text-5xl my-3">
                {row.original.qtd} {row.original.productUnit.name}
              </p>
              <span className="font-sans text-xs text-slate-600 font-normal">
                LOTE: {row.original.batch}
              </span>
              <div className="w-[720px] m-auto mt-6 border-b borber-b-slate-600">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Código</TableHead>
                      <TableHead>Entrada</TableHead>
                      <TableHead>Fabricação</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead className="text-right">Valor Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* {invoices.map((invoice) => ( */}
                    <TableRow key={row.original.id}>
                      <TableCell className="font-medium">
                        {row.original.product.code}
                      </TableCell>
                      <TableCell>
                        {new Date(row.original.entranceDate).toLocaleDateString(
                          'pt-BR'
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(
                          row.original.manufacturingDate
                        ).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        {new Date(
                          row.original.expirationDate
                        ).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        {Formatter.currency(row.original.price)}
                      </TableCell>
                      <TableCell className="text-right">
                        {Formatter.currency(row.original.totalPrice)}
                      </TableCell>
                    </TableRow>
                    {/* ))} */}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        );
        const actions: IDataTableDrawer[] = [
          {
            icon: null,
            content: content,
          },
        ];
        return <DataTableDrawer actions={actions} />;
      },
    },
  ];

  return columns;
}
